/**
 * Congregation API Service
 * Handles all congregation-related API calls
 */

import { ApiClient } from '../core/api-client';
import { ApiError, isApiError } from '../core/errors';
import { logger } from '../../utils/logger';

export interface CongregationMember {
  id: string;
  name: string;
  email: string;
  phone?: string;
  role?: string;
  status?: string;
}

export interface CongregationData {
  id: string;
  name: string;
  description?: string;
  members?: CongregationMember[];
  createdAt?: string;
  updatedAt?: string;
}

export interface CongregationResponse {
  data: CongregationData | null;
  membership?: {
    isMember: boolean;
    joinedAt?: string;
    role?: string;
  };
}

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080/api';

export class CongregationService {
  private client: ApiClient;

  constructor() {
    this.client = ApiClient.getInstance(API_BASE_URL);
  }

  /**
   * Get current user's congregation membership
   * Returns null if user is not a member of any congregation
   */
  async getCurrentUserCongregation(): Promise<CongregationData | null> {
    try {
      const response = await this.client.get<CongregationResponse>(
        '/congregation/me',
        {
          credentials: 'include', // Include cookies for auth
        }
      );

      return response.data?.data || null;
    } catch (error) {
      if (isApiError(error)) {
        // 403 Forbidden - user is not a member of a congregation (this is expected in some cases)
        if (error.statusCode === 403) {
          logger.warn('User is not a member of any congregation');
          return null;
        }

        // Other errors should be thrown
        throw error;
      }

      throw error;
    }
  }

  /**
   * Get specific congregation by ID
   */
  async getCongregationById(congregationId: string): Promise<CongregationData | null> {
    try {
      const response = await this.client.get<CongregationResponse>(
        `/congregation/${congregationId}`,
        {
          credentials: 'include',
        }
      );

      return response.data?.data || null;
    } catch (error) {
      if (isApiError(error) && error.statusCode === 404) {
        logger.warn(`Congregation ${congregationId} not found`);
        return null;
      }

      throw error;
    }
  }

  /**
   * Get all congregations (admin only)
   */
  async listCongregations(limit?: number, offset?: number): Promise<CongregationData[]> {
    try {
      const params = new URLSearchParams();
      if (limit) params.append('limit', limit.toString());
      if (offset) params.append('offset', offset.toString());

      const url = `/congregations${params.toString() ? '?' + params.toString() : ''}`;
      const response = await this.client.get<{ data: CongregationData[] }>(url, {
        credentials: 'include',
      });

      return response.data?.data || [];
    } catch (error) {
      logger.error('Failed to list congregations', error);
      return [];
    }
  }

  /**
   * Get congregation members
   */
  async getCongregationMembers(congregationId: string): Promise<CongregationMember[]> {
    try {
      const response = await this.client.get<{ data: CongregationMember[] }>(
        `/congregation/${congregationId}/members`,
        {
          credentials: 'include',
        }
      );

      return response.data?.data || [];
    } catch (error) {
      if (isApiError(error) && error.statusCode === 404) {
        logger.warn(
          `Members not found for congregation ${congregationId}`
        );
        return [];
      }

      throw error;
    }
  }

  /**
   * Create new congregation (admin only)
   */
  async createCongregation(data: Partial<CongregationData>): Promise<CongregationData> {
    try {
      const response = await this.client.post<CongregationResponse>('/congregation', data, {
        credentials: 'include',
      });

      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }

      return response.data.data;
    } catch (error) {
      logger.error('Failed to create congregation', error);
      throw error;
    }
  }

  /**
   * Update congregation
   */
  async updateCongregation(
    congregationId: string,
    data: Partial<CongregationData>
  ): Promise<CongregationData> {
    try {
      const response = await this.client.put<CongregationResponse>(
        `/congregation/${congregationId}`,
        data,
        {
          credentials: 'include',
        }
      );

      if (!response.data?.data) {
        throw new Error('Invalid response from server');
      }

      return response.data.data;
    } catch (error) {
      logger.error('Failed to update congregation', error);
      throw error;
    }
  }

  /**
   * Delete congregation (admin only)
   */
  async deleteCongregation(congregationId: string): Promise<void> {
    try {
      await this.client.delete(`/congregation/${congregationId}`, {
        credentials: 'include',
      });
    } catch (error) {
      logger.error('Failed to delete congregation', error);
      throw error;
    }
  }

  // ========================================
  // Book Reading Tracking Methods
  // ========================================

  /**
   * List current user's book readings
   */
  async listMyBookReadings(): Promise<any[]> {
    try {
      const response = await this.client.get<{ readings: any[] }>(
        '/congregation/book-readings',
        {
          credentials: 'include',
        }
      );
      return response.data?.readings || [];
    } catch (error) {
      logger.error('Failed to load book readings', error);
      throw error;
    }
  }

  /**
   * Get a specific book reading record
   */
  async getBookReading(id: number): Promise<any> {
    try {
      const response = await this.client.get<{ reading: any }>(
        `/congregation/book-readings/${id}`,
        {
          credentials: 'include',
        }
      );
      return response.data?.reading;
    } catch (error) {
      logger.error('Failed to load book reading', error);
      throw error;
    }
  }

  /**
   * Create a new book reading record
   */
  async createBookReading(input: any): Promise<any> {
    try {
      const response = await this.client.post<{ reading: any }>(
        '/congregation/book-readings',
        input,
        {
          credentials: 'include',
        }
      );
      return response.data?.reading;
    } catch (error) {
      logger.error('Failed to create book reading', error);
      throw error;
    }
  }

  /**
   * Update a book reading record
   */
  async updateBookReading(id: number, input: any): Promise<any> {
    try {
      const response = await this.client.put<{ reading: any }>(
        `/congregation/book-readings/${id}`,
        input,
        {
          credentials: 'include',
        }
      );
      return response.data?.reading;
    } catch (error) {
      logger.error('Failed to update book reading', error);
      throw error;
    }
  }

  /**
   * Delete a book reading record
   */
  async deleteBookReading(id: number): Promise<void> {
    try {
      await this.client.delete(`/congregation/book-readings/${id}`, {
        credentials: 'include',
      });
    } catch (error) {
      logger.error('Failed to delete book reading', error);
      throw error;
    }
  }

  /**
   * List book recommendations for current user
   */
  async listMyBookRecommendations(): Promise<any[]> {
    try {
      const response = await this.client.get<{ recommendations: any[] }>(
        '/congregation/book-recommendations',
        {
          credentials: 'include',
        }
      );
      return response.data?.recommendations || [];
    } catch (error) {
      logger.error('Failed to load book recommendations', error);
      throw error;
    }
  }

  /**
   * List book readings for a member (mentor/admin access)
   */
  async listMemberBookReadings(memberId?: number): Promise<any[]> {
    try {
      const url = memberId
        ? `/congregation/mentor/book-readings?memberId=${memberId}`
        : '/congregation/mentor/book-readings';
      const response = await this.client.get<{ readings: any[] }>(url, {
        credentials: 'include',
      });
      return response.data?.readings || [];
    } catch (error) {
      logger.error('Failed to load member book readings', error);
      throw error;
    }
  }

  /**
   * List book readings for a batch
   */
  async listBatchBookReadings(batchId: number): Promise<any[]> {
    try {
      const response = await this.client.get<{ readings: any[] }>(
        `/congregation/mentor/batches/${batchId}/book-readings`,
        {
          credentials: 'include',
        }
      );
      return response.data?.readings || [];
    } catch (error) {
      logger.error('Failed to load batch book readings', error);
      throw error;
    }
  }

  /**
   * Create a book recommendation
   */
  async createBookRecommendation(input: any): Promise<any> {
    try {
      const response = await this.client.post<{ recommendation: any }>(
        '/congregation/mentor/book-recommendations',
        input,
        {
          credentials: 'include',
        }
      );
      return response.data?.recommendation;
    } catch (error) {
      logger.error('Failed to create book recommendation', error);
      throw error;
    }
  }

  /**
   * List book recommendations for a batch
   */
  async listBatchBookRecommendations(batchId: number): Promise<any[]> {
    try {
      const response = await this.client.get<{ recommendations: any[] }>(
        `/congregation/mentor/batches/${batchId}/book-recommendations`,
        {
          credentials: 'include',
        }
      );
      return response.data?.recommendations || [];
    } catch (error) {
      logger.error('Failed to load batch book recommendations', error);
      throw error;
    }
  }

  /**
   * Delete a book recommendation
   */
  async deleteBookRecommendation(id: number): Promise<void> {
    try {
      await this.client.delete(`/congregation/mentor/book-recommendations/${id}`, {
        credentials: 'include',
      });
    } catch (error) {
      logger.error('Failed to delete book recommendation', error);
      throw error;
    }
  }
}

// Export singleton instance
export const congregationService = new CongregationService();
